import { type EventParticipant, isRegisteredEventParticipant } from 'models';
import { useCallback } from 'react';
import { Avatar, AvatarWrapper, Card } from 'ui';
import { useUser } from 'features/auth';
import { IconDirectMessageButtonNode } from 'features/chat';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { UserLink, UserPopover } from 'features/users/components';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import {
  useGetNodeUserAvatar,
  useGetUserFirstNameWithFallback,
  useGetUserLastNameWithFallback,
  useGetUserNameWithFallback,
} from 'features/users/hooks';
import { useGetInviteesBadgeIcon } from '../../../../components';
import { useEventParticipantsInfiniteQuery } from '../../../../queries';

type P = {
  eventId: string;
};

const EventAdmins = ({ eventId }: P) => {
  const { data: participants, isLoading } = useEventParticipantsInfiniteQuery({
    filter: { eventId, role: 'moderator', rsvp: 'going' },
    pagination: { pageSize: 10 },
  });

  const admins = participants?.pages.flat().flatMap((providersPaginated) => providersPaginated.data);

  const { nodeUserData: nodeViewer } = useUser();
  const getAvatarUrl = useGetNodeUserAvatar();
  const getUserFirstNameWithFallback = useGetUserFirstNameWithFallback();
  const getUserLastNameWithFallback = useGetUserLastNameWithFallback();
  const getUserNameWithFallback = useGetUserNameWithFallback();
  const getBadgeIcon = useGetInviteesBadgeIcon();
  const getProviderAvatarUrl = useCallback(
    (provider: EventParticipant) =>
      isRegisteredEventParticipant(provider) ? getAvatarUrl({ size: 'medium', userId: provider.ref }) : undefined,
    [getAvatarUrl],
  );

  if (admins?.length === 0) {
    return null;
  }

  return (
    <>
      {isLoading || !admins ? (
        <Card.HorizontalScroll.EntitySkeleton />
      ) : (
        <Card.HorizontalScroll
          items={admins.map((provider) => ({
            content: isRegisteredEventParticipant(provider) ? (
              <UserPopover
                fallback={{ ...provider, profilePhoto: getProviderAvatarUrl(provider) }}
                popoverProps={{ stretch: true }}
                userId={provider.ref}
              >
                <UserLink userId={provider.ref}>
                  <AvatarWrapper
                    avatar={{
                      badge: { icon: getBadgeIcon({ rsvp: provider.rsvp }) },
                      photo: getProviderAvatarUrl(provider),
                    }}
                    extra={
                      provider.email ? (
                        <IconDirectMessageButtonNode
                          user={{
                            _id: provider.ref,
                            email: provider.email,
                            firstName: getUserFirstNameWithFallback(provider),
                            lastName: getUserLastNameWithFallback(provider),
                          }}
                          viewer={nodeViewer}
                        />
                      ) : null
                    }
                    user={{ isViewer: provider.ref === nodeViewer._id, name: getUserNameWithFallback(provider) }}
                  />
                </UserLink>
              </UserPopover>
            ) : (
              <Avatar badge={{ icon: getBadgeIcon({ rsvp: provider.rsvp }) }} size={32} />
            ),
            key: provider.ref ?? provider.email,
          }))}
          title="Admins"
        />
      )}
    </>
  );
};

export default EventAdmins;
